import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  FolderKanban,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  Plus,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { BrandLockup } from "@/components/g-logo";
import { AppFooter } from "@/components/app-footer";
import { LanguageSwitcher } from "@/components/language-switcher";
import { LegalLinks } from "@/components/legal-links";
import { RoleSwitcher } from "@/components/role-switcher";
import { cn } from "@/lib/utils";
import { homePathFor, useLogout, useSession, type Role } from "@/lib/session";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles: Role[];
}

function SidebarPanel({
  visible,
  path,
  role,
  userEmail,
  roleClass,
  onNavigate,
  onSignOut,
  signOutPending,
}: {
  visible: NavItem[];
  path: string;
  role: Role | undefined;
  userEmail: string | undefined;
  roleClass: string;
  onNavigate?: () => void;
  onSignOut: () => void;
  signOutPending: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      {role === "grantonomy_admin" && (
        <Button asChild size="sm" className="mb-6 shrink-0">
          <Link to="/projects/new" onClick={onNavigate}>
            <Plus className="mr-1 size-4" /> {t("dashboard.newProject")}
          </Link>
        </Button>
      )}

      <nav className="flex min-h-0 flex-1 flex-col gap-1 text-sm">
        {visible.map((item) => {
          const active =
            item.to === "/" ? path === "/" : path === item.to || path.startsWith(`${item.to}/`);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1.5 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground",
                active && "bg-sidebar-accent text-sidebar-foreground",
              )}
            >
              <item.icon className="size-4 shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto shrink-0 space-y-3 px-2 pt-6">
        <RoleSwitcher />
      </div>

      <div className="mt-4 shrink-0 border-t border-sidebar-border pt-4">
        <div className="mb-2 px-2 text-xs">
          <div className="truncate font-medium">{userEmail ?? "—"}</div>
          <div className="mt-1">
            <span
              data-testid="session-role-badge"
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium tracking-widest ${roleClass}`}
            >
              {role ? t(`appShell.role.${role}`) : t("appShell.role.signedIn")}
            </span>
          </div>
        </div>
        <div className="space-y-2 px-2 pb-2">
          <LanguageSwitcher variant="outline" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={onSignOut}
          disabled={signOutPending}
        >
          <LogOut className="mr-2 size-4" /> {t("common.signOut")}
        </Button>
        <LegalLinks
          testId="legal-sidebar-links"
          className="px-2 pt-3 text-[10px] text-muted-foreground/80"
        />
      </div>
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const { user } = useSession();
  const logout = useLogout();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // G-Admin: Companies nav removed (V-10) — company flow lives on Projects. CA / employee nav unchanged.
  const nav: NavItem[] = [
    {
      to: "/",
      label: t("nav.dashboard"),
      icon: LayoutDashboard,
      roles: ["grantonomy_admin", "company_admin"],
    },
    {
      to: "/projects",
      label: t("nav.projects"),
      icon: FolderKanban,
      roles: ["grantonomy_admin", "company_admin"],
    },
    {
      to: "/employees",
      label: t("appShell.nav.employees"),
      icon: Users,
      roles: ["grantonomy_admin", "company_admin"],
    },
    {
      to: "/my/tracking",
      label: t("appShell.nav.myTracking"),
      icon: CalendarClock,
      roles: ["company_admin", "employee"],
    },
    {
      to: "/festschreibung",
      label: t("appShell.nav.festschreibung"),
      icon: Lock,
      roles: ["company_admin"],
    },
    {
      to: "/admin",
      label: t("nav.admin"),
      icon: ShieldCheck,
      roles: ["grantonomy_admin"],
    },
  ];

  const role = user?.role;
  const visible = role ? nav.filter((item) => item.roles.includes(role)) : [];
  const home = user ? homePathFor(user) : "/";

  const roleClass =
    role === "grantonomy_admin"
      ? "border-black/40 bg-black text-yellow"
      : role === "company_admin"
        ? "border-black/10 bg-yellow text-black"
        : "border-border bg-surface text-muted-foreground";

  function signOut() {
    // Capture before logout clears the session (onSuccess would otherwise see role=undefined).
    const afterSignOut = role === "employee" ? "/employee-login" : "/login";
    logout.mutate(undefined, { onSuccess: () => navigate(afterSignOut, { replace: true }) });
  }

  function closeMobileNav() {
    setMobileNavOpen(false);
  }

  const sidebarPanelProps = {
    visible,
    path,
    role,
    userEmail: user?.email,
    roleClass,
    onSignOut: signOut,
    signOutPending: logout.isPending,
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background px-4 py-3 md:hidden">
        <Link to={home} data-testid="app-brand-mobile" className="min-w-0 shrink">
          <BrandLockup />
        </Link>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              data-testid="mobile-nav-toggle"
              aria-label={t("appShell.openMenu")}
            >
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="flex w-[min(100%,20rem)] flex-col overflow-y-auto bg-sidebar p-4"
            data-testid="mobile-nav-sheet"
            closeLabel={t("appShell.closeMenu")}
          >
            <SheetTitle className="sr-only">{t("appShell.mobileNavTitle")}</SheetTitle>
            <SheetDescription className="sr-only">{t("appShell.mobileNavTitle")}</SheetDescription>
            <Link
              to={home}
              data-testid="app-brand"
              className="mb-6 flex shrink-0 px-2"
              onClick={closeMobileNav}
            >
              <BrandLockup />
            </Link>
            <SidebarPanel {...sidebarPanelProps} onNavigate={closeMobileNav} />
          </SheetContent>
        </Sheet>
      </header>

      <div className="flex flex-1">
        {/*
          Sticky + h-svh keeps the whole sidebar in the viewport while main scrolls.
          Without this, the flex column stretches to page height so logo stays at the
          document top and sign-out/language sit at the document bottom (ticket 65).
        */}
        <aside className="sticky top-0 hidden h-svh w-60 shrink-0 flex-col self-start overflow-y-auto border-r border-border bg-sidebar px-4 py-6 md:flex">
          <Link to={home} data-testid="app-brand" className="mb-8 flex shrink-0 px-2">
            <BrandLockup />
          </Link>
          <SidebarPanel {...sidebarPanelProps} />
        </aside>

        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">{children}</div>
        </main>
      </div>
      <AppFooter />
    </div>
  );
}
